import React, { useContext, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Layout from '../../components/layout/layout'
import Seo, { breadcrumbJsonLd, faqJsonLd, softwareJsonLd, organizationJsonLd } from '../../components/Seo/Seo'
import Reveal from '../../components/Reveal/Reveal'
import TrustStrip from '../../components/TrustStrip/TrustStrip'
import QuoteForm from '../../components/QuoteForm/QuoteForm'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import useEntryMotion from '../../components/motion/useEntryMotion'
import { techFor, relatedCaseStudies, localizeCaseStudy } from '../../data/caseStudies'
import track from '../../lib/track'
import './caseStudy.scss'

const SECTIONS = ['quick', 'overview', 'challenges', 'how', 'delivered', 'results', 'stack', 'faq']

/**
 * One template for every case study. The content lives in
 * src/data/caseStudies.js; this file only lays it out.
 *
 * The shape follows what buyers actually read: quick answers first, then the
 * story (overview, challenges, how it works, what we delivered), then the
 * numbers with their sources, the stack, questions, and a way to start a
 * similar project. Every section is a jump target for the side navigation.
 */
const CaseStudy = ({ study: source }) => {
  const { translations: t, selectedLanguage } = useContext(LanguageContext)
  const study = localizeCaseStudy(source, selectedLanguage)
  const navigate = useNavigate()
  const animateIn = useEntryMotion()
  const [openFaq, setOpenFaq] = useState(0)

  useEffect(() => {
    window.scrollTo(0, 0)
    setOpenFaq(0)
  }, [study.slug])

  const stack = techFor(study.stack)
  const related = relatedCaseStudies(study.slug).map((r) => localizeCaseStudy(r, selectedLanguage))

  const hero = (delay) => ({
    initial: animateIn ? { opacity: 0, y: 18 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  })

  const jump = (id) => {
    const el = document.getElementById(`cs-${id}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const onEstimate = () => {
    track('estimator_click', { from: 'case-study', slug: study.slug })
    navigate(`/estimate?type=${study.estimatorType}`)
  }
  const onCall = () => {
    track('call_click', { from: 'case-study', slug: study.slug })
    navigate('/contact-us#book')
  }

  return (
    <Layout>
      <Seo
        title={study.seoTitle}
        description={study.seoDescription}
        image={study.cover}
        jsonLd={[
          organizationJsonLd(),
          softwareJsonLd({ name: study.title, description: study.summary, image: study.cover, path: study.path }),
          faqJsonLd(study.faq.map((f) => ({ question: f.q, answer: f.a }))),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Projects', path: '/projects' },
            { name: study.title, path: study.path },
          ]),
        ]}
      />

      <article className='cs-page'>
        {/* Hero */}
        <header className='cs-hero'>
          <div className='cs-inner cs-hero-grid'>
            <div className='cs-hero-copy'>
              <motion.nav className='cs-crumbs' aria-label='Breadcrumb' {...hero(0)}>
                <Link to='/'>{t.uiHome}</Link><span>/</span>
                <Link to='/projects'>{t.projects}</Link><span>/</span>
                <span>{study.title}</span>
              </motion.nav>
              <motion.p className='cs-kicker' {...hero(0.05)}>{t.csKicker} · {study.industry}</motion.p>
              <motion.h1 className='cs-title' {...hero(0.1)}>{study.headline}</motion.h1>
              <motion.p className='cs-summary' {...hero(0.16)}>{study.summary}</motion.p>

              <motion.dl className='cs-facts' {...hero(0.22)}>
                <div><dt>{t.csFactBuilt}</dt><dd>{study.built}</dd></div>
                <div><dt>{t.csFactIndustry}</dt><dd>{study.industry}</dd></div>
                <div><dt>{t.csFactRole}</dt><dd>{study.role}</dd></div>
              </motion.dl>

              <motion.div className='cs-hero-actions' {...hero(0.28)}>
                <button type='button' className='cs-btn' onClick={onCall}>{t.csDiscuss} <span aria-hidden='true'>→</span></button>
                <button type='button' className='cs-btn cs-btn-ghost' onClick={onEstimate}>{t.csEstimate}</button>
                {study.liveUrl && (
                  <a className='cs-live' href={study.liveUrl} target='_blank' rel='noreferrer'>{t.csVisit} ↗</a>
                )}
              </motion.div>
            </div>
            <motion.figure className='cs-hero-media' {...hero(0.2)}>
              <img src={study.cover} alt={`${study.title} — ${study.headline}`} />
            </motion.figure>
          </div>
        </header>

        <div className='cs-inner cs-layout'>
          {/* Side navigation */}
          <aside className='cs-nav' aria-label={t.csNavLabel}>
            <p className='cs-nav-label'>{t.csNavLabel}</p>
            <ol>
              {SECTIONS.filter((id) => id !== 'results' || study.metrics.length > 0).map((id) => (
                <li key={id}><button type='button' onClick={() => jump(id)}>{t[`csSection_${id}`]}</button></li>
              ))}
            </ol>
          </aside>

          <div className='cs-body'>
            {/* Quick answers */}
            <section id='cs-quick' className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csSection_quick}</h2>
                <ol className='cs-quick'>
                  {study.quick.map((item, i) => (
                    <li key={item}><span className='cs-num'>{String(i + 1).padStart(2, '0')}</span><p>{item}</p></li>
                  ))}
                </ol>
              </Reveal>
            </section>

            {/* Overview */}
            <section id='cs-overview' className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csSection_overview}</h2>
                <p className='cs-lead'>{study.overview}</p>
              </Reveal>
              {study.gallery.length > 0 && (
                <Reveal className='cs-gallery'>
                  {study.gallery.map((src, i) => (
                    <img key={src} src={src} alt={`${study.title} screenshot ${i + 1}`} loading='lazy' />
                  ))}
                </Reveal>
              )}
            </section>

            {/* Challenges */}
            <section id='cs-challenges' className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csSection_challenges}</h2>
              </Reveal>
              <div className='cs-cards'>
                {study.challenges.map((c, i) => (
                  <Reveal key={c.title} delay={i * 0.05} className='cs-card'>
                    <span className='cs-num'>{String(i + 1).padStart(2, '0')}</span>
                    <h3>{c.title}</h3>
                    <p>{c.text}</p>
                  </Reveal>
                ))}
              </div>
            </section>

            {/* How it works */}
            <section id='cs-how' className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csSection_how}</h2>
              </Reveal>
              <ol className='cs-steps'>
                {study.steps.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.05} className='cs-step'>
                    <span className='cs-step-num'>{i + 1}</span>
                    <div>
                      <h3>{s.title}</h3>
                      <p>{s.text}</p>
                    </div>
                  </Reveal>
                ))}
              </ol>
            </section>

            {/* Delivered */}
            <section id='cs-delivered' className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csSection_delivered}</h2>
              </Reveal>
              <div className='cs-delivered'>
                {study.delivered.map((group, i) => (
                  <Reveal key={group.title} delay={i * 0.05} className='cs-delivered-group'>
                    <h3>{group.title}</h3>
                    <ul>
                      {group.items.map((item) => (
                        <li key={item}><span className='cs-check' aria-hidden='true' />{item}</li>
                      ))}
                    </ul>
                  </Reveal>
                ))}
              </div>
            </section>

            {/* Results: hidden until a project has sourced figures */}
            {study.metrics.length > 0 && (
            <section id='cs-results' className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csSection_results}</h2>
                <p className='cs-hint'>{t.csResultsNote}</p>
              </Reveal>
              <div className='cs-metrics'>
                {study.metrics.map((m, i) => (
                  <Reveal key={m.label} delay={i * 0.05} className='cs-metric'>
                    <span className='cs-metric-value'>{m.value}</span>
                    <span className='cs-metric-label'>{m.label}</span>
                    {m.note && <span className='cs-metric-note'>{m.note}</span>}
                    <span className='cs-metric-source'>{t.csSource}: {m.source}</span>
                  </Reveal>
                ))}
              </div>
            </section>
            )}

            {/* Stack */}
            <section id='cs-stack' className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csSection_stack}</h2>
                <ul className='cs-stack'>
                  {stack.map((tech) => (
                    <li key={tech.name}><img src={tech.src} alt='' loading='lazy' /><span>{tech.name}</span></li>
                  ))}
                </ul>
              </Reveal>
            </section>

            {/* FAQ */}
            <section id='cs-faq' className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csSection_faq}</h2>
              </Reveal>
              <div className='cs-faq'>
                {study.faq.map((f, i) => (
                  <div key={f.q} className={`cs-faq-item ${openFaq === i ? 'is-open' : ''}`}>
                    <button type='button' aria-expanded={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                      <span>{f.q}</span>
                      <span className='cs-faq-icon' aria-hidden='true'>{openFaq === i ? '−' : '+'}</span>
                    </button>
                    {/* Answers stay in the DOM for search engines; only display toggles */}
                    <p className='cs-faq-answer'>{f.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Closing CTA */}
            <section className='cs-section cs-cta'>
              <Reveal>
                <p className='cs-kicker cs-kicker-light'>{t.csCtaKicker}</p>
                <h2 className='cs-cta-title'>{t.csCtaTitle}</h2>
                <p className='cs-cta-text'>{t.csCtaText}</p>
                <div className='cs-hero-actions'>
                  <button type='button' className='cs-btn cs-btn-light' onClick={onEstimate}>{t.csEstimate} <span aria-hidden='true'>→</span></button>
                  <button type='button' className='cs-btn cs-btn-outline' onClick={onCall}>{t.csDiscuss}</button>
                </div>
                <TrustStrip className='cs-trust' />
                <div className='cs-cta-form'>
                  <QuoteForm
                    source={`Case study page: ${source.title}`}
                    subject={`New project enquiry after reading: ${source.title}`}
                    topic={source.title}
                    formName='case-study'
                  />
                </div>
              </Reveal>
            </section>

            {/* Related */}
            <section className='cs-section'>
              <Reveal>
                <h2 className='cs-h2'>{t.csRelated}</h2>
              </Reveal>
              <div className='cs-related'>
                {related.map((r, i) => (
                  <Reveal key={r.slug} delay={i * 0.05}>
                    <Link to={r.path} className='cs-related-card'>
                      <img src={r.cover} alt='' loading='lazy' />
                      <span className='cs-related-kicker'>{r.industry}</span>
                      <span className='cs-related-title'>{r.title}</span>
                      <span className='cs-related-text'>{r.headline}</span>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </section>
          </div>
        </div>
      </article>
    </Layout>
  )
}

export default CaseStudy
